'use client';
import { Link, LinkProps } from '@mui/material';
import React, { PropsWithChildren, useMemo } from 'react';
import { useAnalyticsSkuViewedContext } from '../../Analytics/AnalyticsSkuViewed';
import { CtaType } from '../../Analytics/types';
import { createHandleOnClick, getFinishType } from '../../Analytics/utils';
import { LinkType, MonetizableEnum } from '../../enums';
import { useGetCtaLink } from '../../hooks/useGetCtaLink';
import { useShoppingFinishedId } from '../../hooks/useShoppingFinishedId';
import { AnalyticsType } from '../../types';
import { isPaidBankingRoundupPath, trackCtaClickAction } from '../../utils/trackCtaClickAction';

export interface CtaLinkProps<
  TComponent extends React.ElementType = typeof Link,
> extends LinkProps {
  cta: CtaType;
  analyticsPayload: Omit<AnalyticsType, 'monetizable'>;
  linkType: LinkType;
  monetizable: MonetizableEnum;
  Component?: TComponent | typeof Link;
  /** Override the impression_section value from the SKU viewed context */
  impressionSectionOverride?: string;
}

export function CtaLink<TComponent extends React.ElementType = typeof Link>({
  analyticsPayload,
  cta,
  children,
  Component = Link,
  linkType,
  monetizable,
  impressionSectionOverride,
  ...rest
}: PropsWithChildren<CtaLinkProps<TComponent>>): React.JSX.Element {
  const shoppingFinishedId = useShoppingFinishedId();

  const { onClick: onClickFromProps, ...linkRest } = rest;
  const { index } = analyticsPayload;
  const { fireShoppingFinishedEvent, firePartnerCtaClickEvent } = useAnalyticsSkuViewedContext();
  const ctaUrl = useGetCtaLink({
    href: cta?.link,
    type: linkType,
    monetizable,
    elementPosition: index,
    shoppingFinishedId,
    impressionSectionOverride,
  });

  const finishType = useMemo(() => getFinishType(monetizable), [monetizable]);

  const handleOnClick = () => {
    if (monetizable === MonetizableEnum.yesAssumed) {
      firePartnerCtaClickEvent({
        destinationUrl: ctaUrl,
        ctaType: cta.type != null ? String(cta.type) : 'unknown',
        monetizable,
        overrideShoppingFinishedId: shoppingFinishedId,
      });
    }
    if (monetizable === MonetizableEnum.no) {
      const fireNonMonetizingSFEvent = createHandleOnClick({
        fireShoppingFinishedEvent,
        overrideShoppingFinishedId: shoppingFinishedId,
        destinationUrl: ctaUrl,
        monetizable,
        type: cta.type,
      });

      fireNonMonetizingSFEvent();
    }
  };

  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = event => {
    handleOnClick();
    if (typeof window !== 'undefined' && isPaidBankingRoundupPath(window.location.pathname)) {
      trackCtaClickAction({
        entityName: cta.text,
        shouldFireShoppingFinished: monetizable === MonetizableEnum.no,
        shoppingFinishedId,
        ctaType: cta.type != null ? String(cta.type) : 'unknown',
      });
    }
    onClickFromProps?.(event);
  };

  return (
    <Component
      data-testid="cta-link"
      onClick={handleClick}
      href={ctaUrl}
      component="a"
      rel={`noopener ${finishType === 'external_application' ? 'nofollow' : ''}`}
      target="_blank"
      {...linkRest}
    >
      {children}
    </Component>
  );
}
